import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  TextField,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  CircularProgress,
  Alert,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Chip,
} from '@mui/material';
import SearchIcon from '@mui/icons-material/Search';
import VisibilityIcon from '@mui/icons-material/Visibility';
import PictureAsPdfIcon from '@mui/icons-material/PictureAsPdf';
import TableChartIcon from '@mui/icons-material/TableChart';
import { adminApi } from '../../api/admin';
import {
  downloadExcelBuffer,
  styleExcelHeaderRow,
  todayFileStamp,
  writeLandscapePdfTable,
} from '../../utils/adminReportExport';

const ClientManagement: React.FC = () => {
  const [clients, setClients] = useState<any[]>([]);
  const [filteredClients, setFilteredClients] = useState<any[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedClient, setSelectedClient] = useState<any | null>(null);
  const [detailsOpen, setDetailsOpen] = useState(false);
  const [clientAccounts, setClientAccounts] = useState<any>(null);

  useEffect(() => {
    loadClients();
  }, []);

  const getFineractClientId = (client: any): string | null => {
    const fineractId = client?.accountNumber || client?.accountNo;
    if (fineractId == null || String(fineractId).trim() === '') {
      return null;
    }
    return String(fineractId).trim();
  };

  useEffect(() => {
    if (searchTerm) {
      const filtered = clients.filter(
        (client) =>
          client.displayName?.toLowerCase().includes(searchTerm.toLowerCase()) ||
          client.accountNo?.toLowerCase().includes(searchTerm.toLowerCase()) ||
          client.accountNumber?.toLowerCase().includes(searchTerm.toLowerCase()) ||
          client.email?.toLowerCase().includes(searchTerm.toLowerCase()) ||
          client.id?.toString().includes(searchTerm)
      );
      setFilteredClients(filtered);
    } else {
      setFilteredClients(clients);
    }
  }, [searchTerm, clients]);

  const loadClients = async () => {
    setLoading(true);
    setError('');
    try {
      const data = await adminApi.getAllClients();
      setClients(data);
      setFilteredClients(data);
    } catch (err: any) {
      setError(err.response?.data?.error || 'Failed to load clients');
    } finally {
      setLoading(false);
    }
  };

  const handleViewDetails = async (client: any) => {
    setSelectedClient(client);
    setDetailsOpen(true);
    setClientAccounts(null);
    setLoading(true);
    setError('');
    try {
      // Must use Fineract client id (accountNumber), not local users.id —
      // using the local PK returns another client's loans from Fineract.
      const fineractClientId = getFineractClientId(client);
      if (!fineractClientId) {
        setClientAccounts({ loanAccounts: [], savingsAccounts: [], unlinked: true });
        return;
      }
      const accounts = await adminApi.getClientAccounts(fineractClientId);
      setClientAccounts(accounts);
    } catch (err: any) {
      setError(err.response?.data?.error || 'Failed to load client accounts');
    } finally {
      setLoading(false);
    }
  };

  const handleCloseDetails = () => {
    setDetailsOpen(false);
    setSelectedClient(null);
    setClientAccounts(null);
  };

  const exportToPDF = () => {
    writeLandscapePdfTable({
      title: 'Clients Report',
      subtitle: searchTerm ? `Search: ${searchTerm}` : undefined,
      headers: ['Client ID', 'Account Number', 'Name', 'Email', 'Status', 'Office'],
      colWidths: [25, 35, 50, 50, 30, 40],
      rows: filteredClients.map((client) => [
        String(client.id ?? 'N/A'),
        client.accountNumber || client.accountNo || 'N/A',
        client.displayName || 'N/A',
        client.email || 'N/A',
        client.status?.value || 'N/A',
        client.officeName || 'N/A',
      ]),
      fileName: `clients-report-${todayFileStamp()}.pdf`,
    });
  };

  const exportToExcel = async () => {
    try {
      const ExcelJS = await import('exceljs');
      const workbook = new ExcelJS.Workbook();
      const worksheet = workbook.addWorksheet('Clients');

      worksheet.columns = [
        { header: 'Client ID', key: 'id', width: 12 },
        { header: 'Account Number', key: 'accountNumber', width: 20 },
        { header: 'Name', key: 'displayName', width: 30 },
        { header: 'Email', key: 'email', width: 30 },
        { header: 'Status', key: 'status', width: 14 },
        { header: 'Active', key: 'active', width: 10 },
        { header: 'Office', key: 'officeName', width: 25 },
        { header: 'User Type', key: 'userType', width: 14 },
      ];
      styleExcelHeaderRow(worksheet);

      filteredClients.forEach((client) => {
        worksheet.addRow({
          id: client.id ?? 'N/A',
          accountNumber: client.accountNumber || client.accountNo || 'N/A',
          displayName: client.displayName || 'N/A',
          email: client.email || '',
          status: client.status?.value || 'N/A',
          active: client.status?.active ? 'Yes' : 'No',
          officeName: client.officeName || 'N/A',
          userType: client.userType || '',
        });
      });

      const metadataSheet = workbook.addWorksheet('Export Info');
      metadataSheet.columns = [
        { header: 'Property', key: 'property', width: 25 },
        { header: 'Value', key: 'value', width: 50 },
      ];
      styleExcelHeaderRow(metadataSheet);
      metadataSheet.addRows([
        { property: 'Export Date', value: new Date().toLocaleString() },
        { property: 'Total Clients', value: clients.length },
        { property: 'Exported Records', value: filteredClients.length },
        { property: 'Search Filter', value: searchTerm || 'All' },
      ]);

      const buffer = await workbook.xlsx.writeBuffer();
      await downloadExcelBuffer(buffer, `clients-report-${todayFileStamp()}.xlsx`);
    } catch (err) {
      console.error('Failed to export Excel:', err);
      alert('Failed to export Excel. Please ensure exceljs is installed.');
    }
  };

  return (
    <Box>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3, flexWrap: 'wrap', gap: 2 }}>
        <Typography variant="h5">Client Management</Typography>
        <Box sx={{ display: 'flex', gap: 1, alignItems: 'center', flexWrap: 'wrap' }}>
          <TextField
            placeholder="Search clients..."
            variant="outlined"
            size="small"
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            InputProps={{
              startAdornment: <SearchIcon sx={{ mr: 1, color: 'text.secondary' }} />,
            }}
            sx={{ width: 300 }}
          />
          <Button
            variant="outlined"
            startIcon={<PictureAsPdfIcon />}
            onClick={exportToPDF}
            disabled={loading || filteredClients.length === 0}
            color="error"
          >
            Export PDF
          </Button>
          <Button
            variant="outlined"
            startIcon={<TableChartIcon />}
            onClick={exportToExcel}
            disabled={loading || filteredClients.length === 0}
            color="success"
          >
            Export Excel
          </Button>
        </Box>
      </Box>

      {error && (
        <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError('')}>
          {error}
        </Alert>
      )}

      {loading && clients.length === 0 ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}>
          <CircularProgress />
        </Box>
      ) : (
        <TableContainer component={Paper}>
          <Table>
            <TableHead>
              <TableRow>
                <TableCell>Client ID</TableCell>
                <TableCell>Account Number</TableCell>
                <TableCell>Name</TableCell>
                <TableCell>Status</TableCell>
                <TableCell>Office</TableCell>
                <TableCell>Actions</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {filteredClients.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={6} align="center">
                    <Typography variant="body2" color="text.secondary">
                      No clients found
                    </Typography>
                  </TableCell>
                </TableRow>
              ) : (
                filteredClients.map((client) => (
                  <TableRow key={client.id}>
                    <TableCell>{client.id}</TableCell>
                    <TableCell>{client.accountNumber || client.accountNo || 'N/A'}</TableCell>
                    <TableCell>{client.displayName || 'N/A'}</TableCell>
                    <TableCell>
                      <Chip
                        label={client.status?.value || 'N/A'}
                        color={client.status?.active ? 'success' : 'default'}
                        size="small"
                      />
                    </TableCell>
                    <TableCell>{client.officeName || 'N/A'}</TableCell>
                    <TableCell>
                      <Button
                        size="small"
                        startIcon={<VisibilityIcon />}
                        onClick={() => handleViewDetails(client)}
                      >
                        View Details
                      </Button>
                    </TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
        </TableContainer>
      )}

      <Dialog open={detailsOpen} onClose={handleCloseDetails} maxWidth="md" fullWidth>
        <DialogTitle>Client Details</DialogTitle>
        <DialogContent>
          {selectedClient && (
            <Box sx={{ mt: 2 }}>
              <Typography variant="h6" gutterBottom>
                {selectedClient.displayName}
              </Typography>
              <Typography variant="body2" color="text.secondary" gutterBottom>
                Client ID: {selectedClient.id}
              </Typography>
              <Typography variant="body2" color="text.secondary" gutterBottom>
                Account Number: {selectedClient.accountNumber || selectedClient.accountNo || 'N/A'}
              </Typography>
              <Typography variant="body2" color="text.secondary" gutterBottom>
                Status: {selectedClient.status?.value || 'N/A'}
              </Typography>
              <Typography variant="body2" color="text.secondary" gutterBottom>
                Office: {selectedClient.officeName || 'N/A'}
              </Typography>

              {clientAccounts && (
                <Box sx={{ mt: 3 }}>
                  <Typography variant="h6" gutterBottom>
                    Accounts
                  </Typography>
                  {clientAccounts.unlinked ? (
                    <Typography variant="body2" color="text.secondary">
                      This client has no linked Fineract account, so there are no loans or savings to show.
                    </Typography>
                  ) : (
                    <>
                      <Typography variant="body2" gutterBottom>
                        Loans: {clientAccounts.loanAccounts?.length ?? 0}
                      </Typography>
                      <Typography variant="body2" gutterBottom>
                        Savings: {clientAccounts.savingsAccounts?.length ?? 0}
                      </Typography>
                    </>
                  )}
                </Box>
              )}
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseDetails}>Close</Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default ClientManagement;

